import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, MenuItem, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip, Grid, Divider, Tabs, Tab
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import axios from 'axios';

const fmt = (n) => `S/ ${Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`;
const anios = () => { const y = new Date().getFullYear(); return [y, y-1, y-2].map(String); };

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function ReportesRRHH() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // PLAME
  const [planillas, setPlanillas] = useState([]);
  const [planillaId, setPlanillaId] = useState('');
  const [plameData, setPlameData] = useState(null);

  // Libro Planillas
  const [anio, setAnio] = useState(String(new Date().getFullYear()));
  const [libroPlanillas, setLibroPlanillas] = useState([]);

  useEffect(() => {
    axios.get('/api/rrhh/empresas').then(r => {
      setEmpresas(Array.isArray(r.data) ? r.data : []);
      if (r.data?.length === 1) setEmpresaId(r.data[0].empresa_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) { setPlanillas([]); return; }
    axios.get('/api/rrhh/planillas', { params: { empresa_id: empresaId } })
      .then(r => setPlanillas(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [empresaId]);

  const empresaObj = empresas.find(e => e.empresa_id === empresaId) || null;

  const cargarPlame = async () => {
    if (!planillaId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/rrhh/reportes/plame', { params: { planilla_id: planillaId } });
      setPlameData(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar PLAME'); }
    finally { setLoading(false); }
  };

  const cargarLibro = async () => {
    if (!empresaId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/rrhh/reportes/libro-planillas', { params: { empresa_id: empresaId, anio } });
      setLibroPlanillas(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar libro'); }
    finally { setLoading(false); }
  };

  const descargarPlameTxt = async (sub) => {
    setError('');
    try {
      const r = await axios.get('/api/rrhh/reportes/plame-txt', { params: { planilla_id: planillaId, tipo: sub }, responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const fname = match ? match[1] : `plame.${sub}`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try { setError(JSON.parse(await e.response.data.text()).error || 'Error al generar el archivo'); }
        catch { setError('Error al generar el archivo'); }
      } else setError(e.response?.data?.error || 'Error al generar el archivo');
    }
  };

  const descargarAfpNet = async () => {
    setError('');
    try {
      const r = await axios.get('/api/rrhh/reportes/afp-net', { params: { planilla_id: planillaId }, responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const fname = match ? match[1] : 'afpnet.xlsx';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try { setError(JSON.parse(await e.response.data.text()).error || 'Error al generar el archivo'); }
        catch { setError('Error al generar el archivo'); }
      } else setError(e.response?.data?.error || 'Error al generar el archivo');
    }
  };

  const exportarCsvPlame = () => {
    if (!plameData?.detalle) return;
    const headers = ['N°','Tipo Doc','N° Doc','Apellidos y Nombres','Básico','Asig.Fam','H.Extra','Total Bruto','ONP','AFP','Comisión AFP','Seguro AFP','5ta Cat','Desc.Total','Neto','EsSalud','Prov.CTS','Prov.Gratif'];
    const rows = plameData.detalle.map((d, i) => [
      i+1, d.tipo_doc, d.num_doc,
      `${d.apellido_paterno} ${d.apellido_materno} ${d.nombres}`.trim(),
      d.remuneracion_basica, d.asignacion_familiar, d.importe_horas_extra, d.total_bruto,
      d.aporte_onp, d.aporte_afp_obligatorio, d.comision_afp, d.seguro_afp,
      d.retencion_quinta, d.total_descuentos, d.neto_a_pagar,
      d.aporte_essalud, d.provision_cts, d.provision_gratificacion
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `PLAME_${plameData.planilla?.periodo || ''}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1400 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#222' }}>Reportes RR.HH.</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Box sx={{ mb: 3 }}>
          <Autocomplete
            options={empresas} getOptionLabel={o => o?.razon_social || ''}
            value={empresaObj}
            onChange={(_, v) => setEmpresaId(v ? v.empresa_id : '')}
            isOptionEqualToValue={(o, v) => o.empresa_id === v.empresa_id}
            renderInput={p => <TextField {...p} label="Empresa" size="small" sx={{ minWidth: '16em' }} />}
            sx={{ minWidth: '16em', maxWidth: 400 }}
          />
        </Box>

        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #eee', px: 2, pt: 1 }}>
            <Tab icon={<AssessmentIcon />} label="PLAME" iconPosition="start" />
            <Tab icon={<AssessmentIcon />} label="Libro de Planillas" iconPosition="start" />
            <Tab icon={<AssessmentIcon />} label="AFP NET" iconPosition="start" />
          </Tabs>

          {/* PLAME */}
          <TabPanel value={tab} index={0}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                La PLAME (Planilla Mensual de Pagos) contiene la información de remuneraciones y contribuciones para declarar a SUNAT.
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Los archivos .rem/.jor siguen la estructura de conceptos validada contra una planilla real, pero el código PLAME para la retención de ONP no está confirmado (solo se verificó con trabajadores afiliados a AFP). Si tienes trabajadores en ONP, valida el archivo con tu contable antes de declarar.
              </Alert>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <Autocomplete
                  options={planillas}
                  getOptionLabel={o => o ? `${o.periodo} — ${o.estado}` : ''}
                  value={planillas.find(p => p.id === planillaId) || null}
                  onChange={(_, v) => { setPlanillaId(v ? v.id : ''); setPlameData(null); }}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={p => <TextField {...p} label="Planilla" size="small" sx={{ minWidth: '16em' }} />}
                  sx={{ minWidth: '20em' }}
                />
                <Button variant="contained" onClick={cargarPlame} disabled={!planillaId || loading} startIcon={loading ? <CircularProgress size={16} /> : <AssessmentIcon />}>
                  Generar PLAME
                </Button>
                {plameData?.detalle?.length > 0 && (
                  <>
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportarCsvPlame}>
                      Exportar CSV
                    </Button>
                    <Button variant="outlined" color="secondary" startIcon={<DownloadIcon />} onClick={() => descargarPlameTxt('rem')}>
                      Descargar .rem
                    </Button>
                    <Button variant="outlined" color="secondary" startIcon={<DownloadIcon />} onClick={() => descargarPlameTxt('jor')}>
                      Descargar .jor
                    </Button>
                  </>
                )}
              </Box>

              {plameData && (
                <>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Período</Typography><br /><strong>{plameData.planilla?.periodo}</strong></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Trabajadores</Typography><br /><strong>{plameData.detalle?.length}</strong></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Total Bruto</Typography><br /><strong>{fmt(plameData.planilla?.total_bruto)}</strong></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Neto a Pagar</Typography><br /><strong style={{ color: '#2e7d32' }}>{fmt(plameData.planilla?.total_neto)}</strong></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">EsSalud (Empleador)</Typography><br /><strong>{fmt(plameData.planilla?.total_essalud)}</strong></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Prov. CTS</Typography><br /><strong>{fmt(plameData.planilla?.total_provision_cts)}</strong></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Prov. Gratif.</Typography><br /><strong>{fmt(plameData.planilla?.total_provision_gratif)}</strong></Grid>
                      <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Prov. Vacaciones</Typography><br /><strong>{fmt(plameData.planilla?.total_provision_vacac)}</strong></Grid>
                    </Grid>
                  </Paper>

                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 1100 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                          <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: '#e8f5e9' }}>Trabajador</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Doc.</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pensión</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Básico</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Asig.Fam</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Bruto</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: '#c62828' }}>AFP/ONP</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: '#c62828' }}>5ta Cat</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: '#c62828' }}>T.Desc.</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>Neto</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>EsSalud</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>P.CTS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {plameData.detalle?.map((d, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: '#fff', minWidth: 180 }}>
                              <Typography variant="body2" fontWeight="bold">{d.apellido_paterno} {d.apellido_materno}</Typography>
                              <Typography variant="caption">{d.nombres}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.8em', fontFamily: 'monospace' }}>{d.tipo_doc} {d.num_doc}</TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={d.sistema_pensiones === 'AFP' ? (d.afp_codigo || 'AFP') : 'ONP'} color="primary" variant="outlined" />
                            </TableCell>
                            {[d.remuneracion_basica, d.asignacion_familiar, d.total_bruto].map((v, j) => (
                              <TableCell key={j} align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>{Number(v || 0).toFixed(2)}</TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em', color: '#c62828' }}>
                              {Number((d.aporte_onp || 0) + (d.aporte_afp_obligatorio || 0) + (d.comision_afp || 0) + (d.seguro_afp || 0)).toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em', color: '#c62828' }}>{Number(d.retencion_quinta || 0).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em', color: '#c62828' }}>{Number(d.total_descuentos || 0).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em', fontWeight: 'bold', color: '#2e7d32' }}>{Number(d.neto_a_pagar || 0).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>{Number(d.aporte_essalud || 0).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>{Number(d.provision_cts || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}
            </Box>
          </TabPanel>

          {/* LIBRO DE PLANILLAS */}
          <TabPanel value={tab} index={1}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField select label="Año" size="small" value={anio} onChange={e => setAnio(e.target.value)} sx={{ minWidth: '8em' }}>
                  {anios().map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </TextField>
                <Button variant="contained" onClick={cargarLibro} disabled={!empresaId || loading} startIcon={<AssessmentIcon />}>
                  Generar
                </Button>
              </Box>

              {libroPlanillas.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Período</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>N° Trabajadores</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Bruto</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Descuentos</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>Neto a Pagar</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>EsSalud Empleador</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Prov. CTS</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Prov. Gratif.</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Prov. Vacac.</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {libroPlanillas.map(p => (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ fontWeight: 'bold' }}>{p.periodo}</TableCell>
                          <TableCell align="center">{p.num_trabajadores}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.total_bruto)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#c62828' }}>{fmt(p.total_descuentos)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#2e7d32' }}>{fmt(p.total_neto)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.total_essalud)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.total_provision_cts)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.total_provision_gratif)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.total_provision_vacac)}</TableCell>
                          <TableCell align="center"><Chip size="small" label="Cerrada" color="success" /></TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>TOTALES {anio}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>{libroPlanillas.reduce((s, p) => s + Number(p.num_trabajadores || 0), 0)}</TableCell>
                        {['total_bruto','total_descuentos','total_neto','total_essalud','total_provision_cts','total_provision_gratif','total_provision_vacac'].map(k => (
                          <TableCell key={k} align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                            {fmt(libroPlanillas.reduce((s, p) => s + Number(p[k] || 0), 0))}
                          </TableCell>
                        ))}
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {libroPlanillas.length === 0 && !loading && empresaId && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>Selecciona año y genera el reporte</Typography>
              )}
            </Box>
          </TabPanel>

          {/* AFP NET */}
          <TabPanel value={tab} index={2}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Genera el archivo Excel para declarar y pagar la planilla de aportes en AFPnet (solo trabajadores afiliados a una AFP — los de ONP no van en este archivo).
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Autocomplete
                  options={planillas}
                  getOptionLabel={o => o ? `${o.periodo} — ${o.estado}` : ''}
                  value={planillas.find(p => p.id === planillaId) || null}
                  onChange={(_, v) => setPlanillaId(v ? v.id : '')}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={p => <TextField {...p} label="Planilla" size="small" sx={{ minWidth: '16em' }} />}
                  sx={{ minWidth: '20em' }}
                />
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={descargarAfpNet} disabled={!planillaId}>
                  Descargar Excel AFP NET
                </Button>
              </Box>
            </Box>
          </TabPanel>
        </Paper>
      </Box>
    </Box>
  );
}
